/**
 * Wire schemas for the preventive-care-gap DSI surface (`BACKLOG.md` C12):
 * `GET /api/patients/:patientId/preventive-care-recommendations`,
 * `GET /api/patients/:patientId/preventive-care-recommendations/acknowledgements`, and
 * `POST /api/patients/:patientId/preventive-care-recommendations/:ruleId/acknowledge`.
 *
 * Same reasoning as `clinicalRequests.ts`'s own header, restated because it matters just as much
 * here: these live in `shared/` so the server validates against the exact schema the web app infers
 * its types from, every object schema is strict so an unrecognised key is a rejection rather than a
 * silently ignored extra, and no server-owned value (an id, `orgId`, a timestamp, who acknowledged)
 * appears in any *request* schema — the route layer supplies those from the session, never the body.
 *
 * `preventiveCareRecommendationSchema` mirrors `PreventiveCareRecommendation`
 * (`preventiveCareRules.ts`) field-for-field, including the full `source` object — the DSI
 * transparency requirement this feature exists to satisfy is exactly that this data reach the wire,
 * not just a display string derived from it.
 */

import { z } from 'zod';

import { calendarDateSchema } from './clinicalRequests.ts';
import { PREVENTIVE_CARE_ACK_ACTIONS, PREVENTIVE_CARE_RULE_IDS } from './preventiveCareRules.ts';

export const MAX_ACK_NOTE_LENGTH = 1000;

const dsiSourceAttributesSchema = z.strictObject({
  interventionType: z.literal('rule_based'),
  bibliographicCitation: z.string(),
  interventionDeveloper: z.string(),
  fundingSource: z.string(),
  releaseDate: calendarDateSchema,
  revisionDate: calendarDateSchema,
  dataElementsUsed: z.array(z.string()),
  usesRaceOrEthnicity: z.boolean(),
  usesLanguage: z.boolean(),
  usesSexualOrientationOrGenderIdentity: z.boolean(),
  usesSex: z.boolean(),
  usesDateOfBirth: z.boolean(),
  usesSocialDeterminantsOfHealth: z.boolean(),
  usesHealthStatusAssessments: z.boolean(),
});

const preventiveCareRecommendationBasisDataSchema = z.strictObject({
  dateOfBirth: calendarDateSchema,
  mostRecentAdministeredDate: calendarDateSchema.nullable(),
  matchedImmunizationVaccines: z.array(z.string()),
});

export const preventiveCareRecommendationSchema = z.strictObject({
  ruleId: z.string(),
  vaccineLabel: z.string(),
  patientAgeYears: z.number(),
  summary: z.string(),
  basisData: preventiveCareRecommendationBasisDataSchema,
  source: dsiSourceAttributesSchema,
});
export type PreventiveCareRecommendationResponse = z.infer<
  typeof preventiveCareRecommendationSchema
>;

export const listPreventiveCareRecommendationsResponseSchema = z.strictObject({
  recommendations: z.array(preventiveCareRecommendationSchema),
});
export type ListPreventiveCareRecommendationsResponse = z.infer<
  typeof listPreventiveCareRecommendationsResponseSchema
>;

/** A `ruleId` path segment against the closed rule set — an unrecognised id is a validation failure
 * (400), never silently accepted and then failing to match anything downstream. */
export const preventiveCareRuleIdSchema = z.enum(PREVENTIVE_CARE_RULE_IDS as [string, ...string[]]);

export const preventiveCareAckActionSchema = z.enum(PREVENTIVE_CARE_ACK_ACTIONS);

/**
 * **Deliberately carries no field that could itself write clinical content.** `note` is the
 * clinician's own free-text rationale for the acknowledgement record, not an order, a diagnosis, or a
 * problem/medication entry — see `preventiveCareRoutes.ts`'s own header for why this route never
 * writes to any table but its own acknowledgement log, regardless of `action`.
 */
export const acknowledgePreventiveCareRecommendationRequestSchema = z.strictObject({
  action: preventiveCareAckActionSchema,
  note: z.string().trim().min(1).max(MAX_ACK_NOTE_LENGTH).optional(),
});
export type AcknowledgePreventiveCareRecommendationRequest = z.infer<
  typeof acknowledgePreventiveCareRecommendationRequestSchema
>;

export const preventiveCareAcknowledgementResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  ruleId: z.string(),
  action: preventiveCareAckActionSchema,
  note: z.string().nullable(),
  acknowledgedByUserId: z.uuid(),
  /** The exact recommendation, source attributes included, as it stood at the moment of this
   * decision — see `preventiveCareRoutes.ts` on why this is recomputed and snapshotted server-side,
   * never accepted from the client. */
  recommendationSnapshot: preventiveCareRecommendationSchema,
  createdAt: z.string(),
});
export type PreventiveCareAcknowledgementResponse = z.infer<
  typeof preventiveCareAcknowledgementResponseSchema
>;

export const listPreventiveCareAcknowledgementsResponseSchema = z.strictObject({
  acknowledgements: z.array(preventiveCareAcknowledgementResponseSchema),
});
export type ListPreventiveCareAcknowledgementsResponse = z.infer<
  typeof listPreventiveCareAcknowledgementsResponseSchema
>;
